import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { PaperClipOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Empty,
  Image,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';

import { fetchInterviewForChat } from 'api/interviewChats/client';
import { fetchChannelMessages } from 'api/interviewChats/stream';
import {
  InterviewChatInterview,
  InterviewChatMessage,
  InterviewChatRow,
} from 'api/interviewChats/types';

import {
  INTERVIEW_STATUS_COLORS,
  STATE_COLORS,
  TRANSCRIPT_PAGE_SIZE,
  formatDateTime,
  groupByDay,
  onlyIfCurrent,
  runLoad,
} from 'helpers/interviewChats';

import { useStreamChatClient } from 'hooks';

import Person from '../Person/Person';
import styles from './ChatTranscript.module.scss';

const { Text } = Typography;

interface ChatTranscriptProps {
  chat: InterviewChatRow | null;
  /** Drops the sticky positioning when the panel is rendered inside a drawer. */
  inDrawer?: boolean;
}

const ChatTranscript: React.FC<ChatTranscriptProps> = ({
  chat,
  inDrawer = false,
}) => {
  const apolloClient = useApolloClient();
  const {
    client: streamClient,
    loading: streamLoading,
    error: streamError,
  } = useStreamChatClient();
  const bodyRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<InterviewChatMessage[]>([]);
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interview, setInterview] = useState<InterviewChatInterview | null>(
    null
  );
  const loadGeneration = useRef(0);

  const providerChannelId = chat?.providerChannelId ?? null;
  const latestInterviewId = chat?.latestInterviewId ?? null;
  const host = chat?.host ?? null;
  const guest = chat?.guest ?? null;

  // Newest page first: load the tail of the thread, then let the user walk back.
  useEffect(() => {
    if (!providerChannelId || !host || !guest) {
      loadGeneration.current += 1;
      setMessages([]);
      setOlderCursor(null);
      setError(null);
      return;
    }

    // Wait for the chat connection rather than reporting an empty thread.
    if (!streamClient) return;

    let cancelled = false;
    const generation = ++loadGeneration.current;
    const isCurrent = () => !cancelled && generation === loadGeneration.current;

    // Whatever went wrong on the previous chat has nothing to say about this one.
    setError(null);

    runLoad(
      onlyIfCurrent(setLoading, isCurrent),
      onlyIfCurrent(setError, isCurrent),
      'Failed to load the chat transcript.',
      async () => {
        const page = await fetchChannelMessages(streamClient, {
          providerChannelId,
          host,
          guest,
          limit: TRANSCRIPT_PAGE_SIZE,
        });
        if (!isCurrent()) return;
        setMessages(page.items);
        setOlderCursor(page.olderCursor);
      }
    );

    return () => {
      cancelled = true;
    };
    // host/guest are read off the same row as providerChannelId, so the id covers them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamClient, providerChannelId]);

  useEffect(() => {
    if (!latestInterviewId) {
      setInterview(null);
      return;
    }

    let cancelled = false;

    // Context only — a failure here must not blank out the transcript.
    fetchInterviewForChat(apolloClient, latestInterviewId)
      .then((result) => {
        if (!cancelled) setInterview(result);
      })
      .catch(() => {
        if (!cancelled) setInterview(null);
      });

    return () => {
      cancelled = true;
    };
  }, [apolloClient, latestInterviewId]);

  // Land on the newest message, the way a chat app does.
  useEffect(() => {
    if (loading || streamLoading || !bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [loading, streamLoading, providerChannelId]);

  const handleLoadOlder = async () => {
    if (!providerChannelId || !olderCursor || !streamClient || !host || !guest)
      return;

    const body = bodyRef.current;
    const previousHeight = body?.scrollHeight ?? 0;

    const generation = loadGeneration.current;
    const isCurrent = () => generation === loadGeneration.current;

    let succeeded = false;
    await runLoad(
      onlyIfCurrent(setLoadingOlder, isCurrent),
      onlyIfCurrent(setError, isCurrent),
      'Failed to load older messages.',
      async () => {
        const page = await fetchChannelMessages(streamClient, {
          providerChannelId,
          host,
          guest,
          limit: TRANSCRIPT_PAGE_SIZE,
          beforeId: olderCursor,
        });
        if (!isCurrent()) return;
        setMessages((current) => [...page.items, ...current]);
        setOlderCursor(page.olderCursor);
        succeeded = true;
      }
    );

    // Hold the reading position steady after prepending older messages.
    // Skip when the load failed, since scrollHeight hasn't changed and the
    // math below would otherwise jump the view back to the top.
    if (body && succeeded) {
      window.requestAnimationFrame(() => {
        body.scrollTop = body.scrollHeight - previousHeight;
      });
    }
  };

  const dayGroups = useMemo(() => groupByDay(messages), [messages]);

  const className = [styles.transcript, inDrawer ? styles.transcriptDrawer : '']
    .filter(Boolean)
    .join(' ');

  if (!chat) {
    return (
      <div className={className}>
        <div className={styles.transcriptPlaceholder}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Pick a chat to read the conversation."
          />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className={styles.transcriptHeader}>
        <div className={styles.transcriptPeople}>
          <Person person={chat.host} size={28} fallbackLabel="No host" />
          <span className={styles.transcriptArrow}>↔</span>
          <Person person={chat.guest} size={28} fallbackLabel="No guest" />
          {chat.state && (
            <Tag color={STATE_COLORS[chat.state]}>{chat.state}</Tag>
          )}
        </div>

        <div className={styles.transcriptMeta}>
          <span>
            {chat.messageCount} message{chat.messageCount === 1 ? '' : 's'}
          </span>
          <span>·</span>
          <Tooltip title="Chat created">
            <span>{formatDateTime(chat.createdAt)}</span>
          </Tooltip>
          {interview && (
            <>
              <span>·</span>
              <Tag color={INTERVIEW_STATUS_COLORS[interview.status]}>
                {interview.status}
              </Tag>
              {interview.scheduleEvent?.startTs && (
                <Tooltip title="Interview start">
                  <span>{formatDateTime(interview.scheduleEvent.startTs)}</span>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>

      {(error || streamError) && (
        <Alert
          type="error"
          message={error || streamError}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      <div className={styles.transcriptBody} ref={bodyRef}>
        {loading || streamLoading ? (
          <div className={styles.transcriptLoading}>
            <Spin />
          </div>
        ) : !messages.length ? (
          <div className={styles.transcriptPlaceholder}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No messages — this chat was provisioned but never used."
            />
          </div>
        ) : (
          <>
            {olderCursor && (
              <Button
                size="small"
                className={styles.loadOlder}
                loading={loadingOlder}
                onClick={handleLoadOlder}
              >
                Load older messages
              </Button>
            )}

            {dayGroups.map((group) => (
              <React.Fragment key={group.day}>
                <div className={styles.daySeparator}>{group.label}</div>
                {group.messages.map((message) =>
                  message.role === 'System' ? (
                    <div key={message.id} className={styles.systemMessage}>
                      {message.text || 'Channel event'}
                    </div>
                  ) : (
                    <div
                      key={message.id}
                      className={[
                        styles.messageRow,
                        message.role === 'Guest'
                          ? styles.messageRowGuest
                          : styles.messageRowHost,
                      ].join(' ')}
                    >
                      <div
                        className={[
                          styles.bubble,
                          message.role === 'Guest' ? styles.bubbleGuest : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <div className={styles.bubbleHeader}>
                          <span className={styles.bubbleAuthor}>
                            {message.userName || message.userId || 'Unknown'}
                          </span>
                          {message.role === 'Other' && <Tag>Support</Tag>}
                          <span className={styles.bubbleTime}>
                            {moment(message.createdAt).format('HH:mm')}
                          </span>
                        </div>

                        {message.isDeleted ? (
                          <div
                            className={[
                              styles.bubbleText,
                              styles.bubbleDeleted,
                            ].join(' ')}
                          >
                            Message deleted
                          </div>
                        ) : (
                          message.text && (
                            <div className={styles.bubbleText}>
                              {message.text}
                            </div>
                          )
                        )}

                        {message.attachments.length > 0 && (
                          <div className={styles.bubbleAttachments}>
                            <Image.PreviewGroup>
                              {message.attachments.map((attachment, index) =>
                                attachment.kind === 'image' ? (
                                  <Image
                                    key={index}
                                    className={styles.attachmentImage}
                                    src={attachment.thumbUrl ?? undefined}
                                    alt={attachment.name ?? 'Image'}
                                    preview={{
                                      src: attachment.url ?? undefined,
                                    }}
                                  />
                                ) : attachment.url ? (
                                  <a
                                    key={index}
                                    className={styles.attachmentFile}
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <PaperClipOutlined />
                                    {attachment.name || 'Attachment'}
                                  </a>
                                ) : (
                                  <span
                                    key={index}
                                    className={styles.attachmentFile}
                                  >
                                    <PaperClipOutlined />
                                    {attachment.name || 'Attachment'}{' '}
                                    (unavailable)
                                  </span>
                                )
                              )}
                            </Image.PreviewGroup>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      <div className={styles.transcriptFooter}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {messages.length} message{messages.length === 1 ? '' : 's'} loaded
          {olderCursor ? ' · older history available' : ''}
        </Text>
        <Tooltip title="GetStream channel ID — use it to find this chat in GetStream or the logs.">
          <Text
            type="secondary"
            style={{ fontSize: 12 }}
            copyable={{ text: chat.providerChannelId }}
          >
            <span className={styles.channelIdLabel}>Channel ID:</span>{' '}
            {chat.providerChannelId}
          </Text>
        </Tooltip>
      </div>
    </div>
  );
};

export default ChatTranscript;
